import type { PermissionAction, ScreenId } from "@agentcall/core/type/permission_type"
import { useAtomValue } from "jotai"
import { useEffect } from "react"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { SectionError } from "@/components/ui/error"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { isIpNotAllowedError } from "@/features/ip-restriction"
import { isSessionValidAtom, sessionErrorAtom, sessionLoadingAtom } from "@/store/session"
import { usePermission } from "../hooks/usePermission"

const SKIP_PERMISSION_CHECK_PATHS = ["/forbidden", "/dashboard", "/system", "/profile"]

// 具体的なパスを先に判定する。
const PATH_PATTERNS: [RegExp, ScreenId][] = [
  [/^\/properties\/\d+\/rooms/, "rooms"], // 部屋情報管理（物件配下）
  [/^\/properties/, "properties"], // 物件情報管理
  [/^\/tenants/, "tenants"], // 賃借人管理（レポート出力含む）
  [/^\/owners/, "owners"], // オーナー管理
  [/^\/tickets/, "tickets"], // 案件管理
  [/^\/tenant-applicants/, "tenant_applicants"], // 新規入居者申込
  [/^\/admin-users/, "admin_users"], // アカウント管理
  [/^\/roles/, "roles"], // ロール管理
  [/^\/organizations/, "organizations"], // 組織管理
  // /system, /dashboard, /profile は権限対象外（全ロールアクセス可能）
]

function getScreenIdFromPath(pathname: string): ScreenId | null {
  for (const [pattern, screenId] of PATH_PATTERNS) {
    if (pattern.test(pathname)) {
      return screenId
    }
  }
  return null
}

function getRequiredActionFromPath(pathname: string): PermissionAction {
  if (/\/add$/.test(pathname)) {
    return "create"
  }
  if (/\/edit$/.test(pathname)) {
    return "update"
  }
  return "read"
}

export function ProtectedRoute() {
  const isSessionValid = useAtomValue(isSessionValidAtom)
  const isLoading = useAtomValue(sessionLoadingAtom)
  const sessionError = useAtomValue(sessionErrorAtom)
  const location = useLocation()
  const navigate = useNavigate()
  const {
    can,
    isLoading: isPermissionLoading,
    isError: isPermissionError,
    refetch: refetchPermission,
  } = usePermission()

  useEffect(() => {
    // IP アドレス制限による拒否は API クライアントが専用ページへ移すため、ログイン画面へは送らない
    if (isIpNotAllowedError(sessionError)) {
      return
    }
    if (!isLoading && !isSessionValid) {
      toast.error("セッションが切れました。再度ログインしてください。", {
        id: "session-expired",
      })
      const redirectPath = location.pathname + location.search
      navigate(`/signin?redirect=${encodeURIComponent(redirectPath)}`, { replace: true })
    }
  }, [isLoading, isSessionValid, sessionError, navigate, location])

  if (isLoading || !isSessionValid || isPermissionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="text-slate-600 size-6" />
      </div>
    )
  }

  const shouldSkipPermissionCheck = SKIP_PERMISSION_CHECK_PATHS.some((path) =>
    location.pathname.startsWith(path)
  )
  if (shouldSkipPermissionCheck) {
    return <Outlet />
  }

  if (isPermissionError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <SectionError
          title="権限情報を取得できませんでした"
          message="通信環境を確認して再読み込みしてください。"
          onRetry={() => refetchPermission()}
        />
      </div>
    )
  }

  const screenId = getScreenIdFromPath(location.pathname)
  const requiredAction = getRequiredActionFromPath(location.pathname)

  if (screenId && !can(screenId, requiredAction)) {
    return <Navigate to="/forbidden" replace />
  }

  return <Outlet />
}
